import React, { useEffect, useMemo } from 'react'
// @ts-ignore no types
import { merge, cloneDeep } from 'lodash'
import type {
  primitives,
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintLiveReactiveArgs,
  OnlineAuctionsDomainInput,
  LiveAuctionsDomainInput,
} from '@adc/blueprint'
import type { Listing, Maybe, AuctionSegment, Property } from '@adc/sdk/types'
import type { FeatureFlags } from '@adc/ui-adc'
// @ts-ignore no types
import { connect } from 'react-redux'
import * as graph from '@adc/sdk'
// @ts-ignore no types
import { hooks, useApplicationContext } from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import {
  CombinedListingType,
  ListingPageActionsType,
} from 'common/context/listing'
import AdvertisementMarketingScript from 'common/components/advertisement/advertisementMarketingScript'
import { PropertyPageActionsType } from 'common/context/property'
import type { ChoosePathModalProps } from 'common/modules/redesign/choosePathModal'
import { fetchRealtyTrackReport } from '../actions/propertyAnalytics'
import { REDIRECT_ACTION_KEY } from '../statics'
import TPSOnlineBidBanner from '../modules/tpsOnlineBidBanner'
import REOBidBanner from './reoBidBanner'

type AuctionUser = primitives.AuctionUser

interface ApplicationBidBannerProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  segment: Maybe<AuctionSegment | AuctionBlueprintOnlineReactiveArgs['segment']>
}

/**
 * This function should be able to handle listing and segment types for online reo, tps online, or private seller (sealed bid segment type)
 * @param param0
 * @returns
 */
export const ApplicationBidBanner = ({
  listing,
  segment,
}: ApplicationBidBannerProps) => {
  // don't show bid banner for live segments OR sealed bid online segments
  if (
    segment &&
    (segment.__typename === 'LiveAuctionSegment' ||
      (segment.__typename === 'OnlineAuctionSegment' &&
        segment.segment_type ===
          graph.enums.OnlineAuctionSegmentType.SealedBid))
  )
    return null

  if (
    listing.listing_configuration?.asset_type ===
    graph.enums.AssetType.Foreclosure
  ) {
    return <TPSOnlineBidBanner />
  } else {
    return <REOBidBanner />
  }
}

interface PropertyWithSegmentProps {
  initialListing: Listing
  featureFlags: FeatureFlags
  pageBannerRef: any
  user: AuctionUser
  childComponent: React.ElementType
  isOnline: boolean
  biddingParams: LiveAuctionsDomainInput | OnlineAuctionsDomainInput
  graphWebsocket: any
  pathModal: ChoosePathModalProps
}
interface PropertyContainerProps {
  initialListing: Listing
  activeListings: Listing[]
  listingActions: ListingPageActionsType
  isPropertyReportAvailable: boolean
  fetchRealtyTrackReport: (listingId: string) => void
  featureFlags: FeatureFlags
  pageBannerRef: any
  user: AuctionUser
  childComponent: React.ElementType
  property: Property
  propertyActions: PropertyPageActionsType
  pathModal: ChoosePathModalProps
}

export const ActivePropertyWrapper = ({
  initialListing,
  isPropertyReportAvailable,
  fetchRealtyTrackReport,
  featureFlags,
  pageBannerRef,
  user,
  childComponent,
  activeListings,
  listingActions,
  property,
  propertyActions,
  pathModal,
  ...rest
}: PropertyContainerProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const { graphWebsocket } = useApplicationContext()

  const memoizedRest = useMemo(() => rest, [rest])

  const isOnline = Boolean(initialListing.auction?.is_online)

  const initialData = useMemo(
    () => ({
      listings: activeListings,
      // TODO add live_auction_segment management
      segments: activeListings
        .map(
          listing =>
            listing[
              listing.auction?.is_online
                ? 'online_auction_segment'
                : 'live_auction_segment'
            ]
        )
        .filter(Boolean),
    }),
    [activeListings]
  )
  const biddingParams = {
    // @ts-ignore WIP
    initialData,
    // @ts-ignore global var
    enabled: __CLIENT_BUNDLE__,
    graphWebsocket,
    user,
  }

  hooks.useMountEffect(() => {
    if (isPropertyReportAvailable)
      fetchRealtyTrackReport(initialListing.listing_id)
    localStorage.removeItem(REDIRECT_ACTION_KEY)
  })

  const previousIsPropertyReportAvailable = hooks.usePrevious(
    isPropertyReportAvailable
  )

  useEffect(() => {
    if (isPropertyReportAvailable && !previousIsPropertyReportAvailable)
      fetchRealtyTrackReport(initialListing.listing_id)

    // remove redirect intent in this parent component because if none of the child components consume it, then it should be removed to avoid possibility that
    // it pollutes the interaction with a different listing
    if (localStorage?.getItem(REDIRECT_ACTION_KEY)) {
      localStorage.removeItem(REDIRECT_ACTION_KEY)
    }
  }, [
    isPropertyReportAvailable,
    fetchRealtyTrackReport,
    previousIsPropertyReportAvailable,
    memoizedRest,
  ])

  const PropertyWithSegment = isOnline
    ? PropertyWithOnlineListing
    : PropertyWithLiveListing

  // TODO: check if segment is used in any components that assume it's live auction segment and point those to live_segment instead, once sure it's safe, change segment to only populate if it's OnlineAuctionSegment typename
  return (
    <PropertyWithSegment
      childComponent={childComponent}
      // @ts-ignore WIP
      biddingParams={biddingParams}
      initialListing={initialListing}
      graphWebsocket={graphWebsocket}
      featureFlags={featureFlags}
      pageBannerRef={pageBannerRef}
      user={user}
      isOnline={isOnline}
      activeListings={activeListings}
      listingActions={listingActions}
      property={property}
      propertyActions={propertyActions}
      pathModal={pathModal}
    />
  )
}

const PropertyWithOnlineListing = ({
  biddingParams,
  initialListing,
  childComponent: PropertyContent,
  ...restProps
}: PropertyWithSegmentProps) => {
  const bidding = blueprint.bidding.hooks.useOnlineAuctions(
    biddingParams as OnlineAuctionsDomainInput
  )
  const segment = bidding?.data?.segments?.mapped.get(
    String(initialListing.listing_id)
  )

  // the merge here is a temporary fix until we can diagnose and fix the root cause within blueprint. To reproduce the issue, check listing.is_current_user_allowed on what returns from blueprint when you go from logged out to logged in. At first it correctly merges with the initialListing, but then it disappears, hence the need to remerge whatever gets emitted from blueprint here
  const listing: AuctionBlueprintOnlineReactiveArgs['listing'] = merge(
    cloneDeep(initialListing),
    bidding?.data?.listings.mapped.get(initialListing.listing_id)
  )

  return (
    <>
      <AdvertisementMarketingScript listing={listing as CombinedListingType} />
      <PropertyContent {...restProps} segment={segment} listing={listing} />
    </>
  )
}

const PropertyWithLiveListing = ({
  biddingParams,
  initialListing,
  childComponent: PropertyContent,
  ...restProps
}: PropertyWithSegmentProps) => {
  const bidding = blueprint.bidding.hooks.useLiveAuctions(
    biddingParams as LiveAuctionsDomainInput
  )
  const segment = bidding?.data?.segments?.mapped.get(
    String(initialListing.listing_id)
  )

  // the merge here is a temporary fix until we can diagnose and fix the root cause within blueprint. To reproduce the issue, check listing.is_current_user_allowed on what returns from blueprint when you go from logged out to logged in. At first it correctly merges with the initialListing, but then it disappears, hence the need to remerge whatever gets emitted from blueprint here
  const listing: AuctionBlueprintLiveReactiveArgs['listing'] = merge(
    cloneDeep(initialListing),
    bidding?.data?.listings.mapped.get(initialListing.listing_id)
  )

  return (
    <>
      <AdvertisementMarketingScript listing={listing as CombinedListingType} />
      <PropertyContent {...restProps} segment={segment} listing={listing} />
    </>
  )
}

const mapStateToProps = (store: any) => ({
  isPropertyReportAvailable: store.propertyAnalytics.isPropertyReportAvailable,
  featureFlags: store.featureFlags,
  user: store.user,
})

const mapDispatchToProps = {
  fetchRealtyTrackReport: (listingId: string) =>
    fetchRealtyTrackReport(listingId),
}

const ActivePropertyWrapperContainer = connect<
  {
    isPropertyReportAvailable: boolean
    featureFlags: FeatureFlags
    user: AuctionUser
  },
  { fetchRealtyTrackReport: (listingId: string) => void },
  any
>(
  mapStateToProps,
  mapDispatchToProps
)(ActivePropertyWrapper)

export default ActivePropertyWrapperContainer
